/**
 * Azari Image Scroll - High Performance Sticky Stack Animation
 * Optimized for smooth scrolling with pre-calculated positions and CSS custom properties
 */

class AzariImageScroll {
  constructor(container) {
    this.container = container;
    this.imageContainer = container.querySelector(
      ".azari-image-scroll__images"
    );
    this.slides = Array.from(
      container.querySelectorAll(".azari-image-scroll__slide")
    );
    this.spacer = container.querySelector(".azari-image-scroll__spacer");

    this.isActive = false;
    this.cachedData = null;
    this.rafId = null;
    this.lastKnownScrollY = window.scrollY;

    if (this.slides.length === 0) return;

    this.init();
  }
  init() {
    // Add classes for mobile image detection
    this.detectMobileImages();

    // Pre-calculate all positions and cache them
    this.calculateLayout();

    // Set up event listeners
    this.setupEventListeners();

    // Initial render
    this.render();
  }

  detectMobileImages() {
    // Add class to slides that don't have mobile images
    this.slides.forEach((slide) => {
      const mobileImage = slide.querySelector(
        ".azari-image-scroll__image--mobile"
      );
      if (!mobileImage) {
        slide.classList.add("no-mobile-image");
      }
    });
  }
  calculateLayout() {
    const containerRect = this.container.getBoundingClientRect();
    const containerTop = containerRect.top + window.scrollY;

    // Calculate viewport dimensions
    const viewportHeight = window.innerHeight; // Calculate scroll distances needed for smooth transitions
    const transitionDistance = viewportHeight * 0.8; // Reduced for smoother transitions
    const safeSpace = 50; // Extra space to ensure full visibility at the end
    const totalScrollDistance =
      transitionDistance * (this.slides.length - 1) + safeSpace;

    // Set spacer height to create enough scroll distance with multiple methods for reliability
    this.spacer.style.height = `${totalScrollDistance}px`;
    this.spacer.style.minHeight = `${totalScrollDistance}px`;
    this.spacer.style.paddingBottom = `${totalScrollDistance}px`;

    // Force spacer height with inner content to ensure it takes up space
    if (!this.spacer.querySelector(".spacer-inner")) {
      const spacerInner = document.createElement("div");
      spacerInner.className = "spacer-inner";
      spacerInner.style.height = `${totalScrollDistance}px`;
      spacerInner.style.display = "block";
      spacerInner.style.pointerEvents = "none";
      this.spacer.appendChild(spacerInner);
    }

    // Calculate when this section becomes active
    const activationStart = containerTop - viewportHeight * 0.5;
    const activationEnd =
      containerTop + containerRect.height + totalScrollDistance;

    // Cache all the data we need for scroll calculations
    this.cachedData = {
      containerTop,
      containerHeight: containerRect.height,
      viewportHeight,
      transitionDistance,
      totalScrollDistance,
      activationStart,
      activationEnd,
      slideCount: this.slides.length,
      // Pre-calculate positions for each slide
      slidePositions: this.slides.map((_, index) => ({
        startProgress: index / (this.slides.length - 1),
        endProgress: (index + 1) / (this.slides.length - 1),
        translateStart: index * transitionDistance,
        translateEnd: (index + 1) * transitionDistance,
      })),
    };
  }

  setupEventListeners() {
    // Use passive scroll listener for better performance
    window.addEventListener("scroll", this.handleScroll.bind(this), {
      passive: true,
    });

    // Recalculate on resize with debouncing
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.calculateLayout();
        this.render();
      }, 150);
    });
  }

  handleScroll() {
    // Only request animation frame if we're not already doing so
    if (!this.rafId) {
      this.rafId = requestAnimationFrame(() => {
        this.render();
        this.rafId = null;
      });
    }
  }

  render() {
    if (!this.cachedData) return;

    const currentScrollY = window.scrollY;
    const {
      activationStart,
      activationEnd,
      containerTop,
      totalScrollDistance,
      transitionDistance,
      slidePositions,
    } = this.cachedData;

    // Check if we're in the active scroll zone
    const wasActive = this.isActive;
    this.isActive =
      currentScrollY >= activationStart && currentScrollY <= activationEnd;

    // If not active, make sure we're in default state
    if (!this.isActive) {
      if (wasActive) {
        // We just left the active zone, reset to default
        this.resetToDefault();
      }
      return;
    }

    // Calculate overall progress through the sticky section
    // Use the actual transition distance (without safe space) for proper progress calculation
    const effectiveScrollDistance =
      transitionDistance * (this.slides.length - 1);
    const sectionProgress = Math.max(
      0,
      Math.min(1, (currentScrollY - containerTop) / effectiveScrollDistance)
    );

    // Calculate and update each slide position
    this.updateSlidePositions(sectionProgress);

    // Store scroll position for next frame
    this.lastKnownScrollY = currentScrollY;
  }
  updateSlidePositions(progress) {
    const { slidePositions, viewportHeight } = this.cachedData;

    this.slides.forEach((slide, index) => {
      const slideData = slidePositions[index];
      let slideProgress = 0;
      let opacity = 1;
      let zIndex = this.slides.length - index;
      if (index === 0) {
        // First slide: starts visible, fades out completely as we scroll
        slideProgress = Math.max(0, Math.min(1, progress * this.slides.length));
        opacity = Math.max(0, 1 - slideProgress); // Allow full transparency
      } else {
        // Other slides: calculate when they should appear
        const startAt = (index - 1) / (this.slides.length - 1);
        const endAt = index / (this.slides.length - 1);

        if (progress < startAt) {
          opacity = 0;
          slideProgress = 0;
        } else if (progress > endAt) {
          opacity = 1;
          slideProgress = 1;
        } else {
          const localProgress = (progress - startAt) / (endAt - startAt);
          slideProgress = localProgress;
          opacity = localProgress; // Allow full range from 0 to 1
        }
      }

      // Use CSS custom properties for GPU-accelerated transforms
      slide.style.setProperty("--slide-opacity", opacity);
      slide.style.setProperty("--slide-z-index", zIndex);

      // Enhanced parallax effect for more dynamic movement
      const currentTransform =
        slide.style.getPropertyValue("--slide-transform") || "0";
      const newTransform = slideProgress * viewportHeight * 0.05; // Reduced for subtlety

      if (Math.abs(parseFloat(currentTransform) - newTransform) > 0.1) {
        slide.style.setProperty("--slide-transform", `${newTransform}px`);
      }
    });
  }

  resetToDefault() {
    // Reset all slides to their default state
    this.slides.forEach((slide, index) => {
      slide.style.setProperty("--slide-opacity", index === 0 ? "1" : "0");
      slide.style.setProperty("--slide-z-index", this.slides.length - index);
      slide.style.setProperty("--slide-transform", "0px");
    });
  }
}

// Initialize all Azari Image Scroll sections
document.addEventListener("DOMContentLoaded", () => {
  const sections = document.querySelectorAll(".azari-image-scroll");
  sections.forEach((section) => new AzariImageScroll(section));
});

// Re-initialize on theme section loads (for Shopify theme editor)
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.classList.contains("azari-image-scroll")) {
    new AzariImageScroll(event.target);
  }
});
